import { useTranslation } from 'react-i18next';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { DELIVERY_TABS } from '@/Pages/tabRoutes';
import DispatchBoardPanel from './components/DispatchBoardPanel';
import DriverShiftsPanel from './components/DriverShiftsPanel';
import DriversPanel from './components/DriversPanel';
import ZonesPanel from './components/ZonesPanel';
import TrackingPolicyPanel from './components/TrackingPolicyPanel';
import AnalyticsPanel from './components/AnalyticsPanel';

/**
 * §16 own delivery & driver operations (ADR-0029) — 31 backend routes across
 * 7 controllers, previously reachable from nowhere in this Dashboard.
 *
 * ## Why six tabs and not six screens, and not one
 *
 * The routes group into distinct jobs done by distinct people at distinct
 * moments — a dispatcher watching live deliveries all shift is not the same
 * person, at the same time, as a manager reconciling a driver's cash at
 * handover, who is not the same person as whoever prices a delivery zone
 * once at setup. Grouping every route onto one page regardless of that would
 * be the exact "same module, different jobs" mistake the task warns against.
 *
 * But this codebase already has a working answer for "one epic, several
 * distinct jobs, several audiences" — Fleet (spool vs. registry vs. hardware
 * vs. version floor), Risk (queue vs. rules vs. reviewers) and Workforce
 * (staff vs. timesheets vs. exceptions vs. policy) all resolve it as one
 * nav row with routed tabs, not one row per job. Each tab here is its own
 * component with its own data, its own loading/error state and its own
 * permission gate — nothing is rendered side by side, so "do not merge them
 * into one page" is honoured at the component level, not just glossed over
 * by a shared URL prefix. Six new rail rows for one epic, next to Fleet's
 * five and Risk's three, would be the rail bloat the tab convention exists
 * to prevent.
 *
 * | Tab | Job | Who | Realtime |
 * |---|---|---|---|
 * | `board` | Assign, progress and cancel a delivery | Dispatcher (`deliveries.dispatch`/`.update_status`), or a driver's own assignment (`deliveries.view`+`.update_status` only) | Subscribed — `delivery.status_changed` is forwarded to sockets |
 * | `shifts` | Open/close own shift; reconcile a driver's cash | Driver (self-service) or manager (`drivers.view`) | None — no `VENUE_EVENTS` entry exists for a shift; opened at handover, not left running |
 * | `drivers` | Roster | Dispatch tier (`drivers.view`/`.manage`) | — |
 * | `zones` | Coverage areas and fees | Dispatch tier (`delivery_zones.*`) | — |
 * | `tracking` | Location-capture policy (opt-in, off by default) | `delivery_location.configure` only | — |
 * | `analytics` | Delivery performance | `deliveries.view` | — |
 */
export default function Delivery() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/delivery', DELIVERY_TABS);

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('delivery.title')}</h1>
        <p className="text-sm text-ink-muted">{t('delivery.subtitle')}</p>
      </div>

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('delivery.title')}
        idPrefix="delivery"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />

      {current === 'board' && <DispatchBoardPanel />}
      {current === 'shifts' && <DriverShiftsPanel />}
      {current === 'drivers' && <DriversPanel />}
      {current === 'zones' && <ZonesPanel />}
      {current === 'tracking' && <TrackingPolicyPanel />}
      {current === 'analytics' && <AnalyticsPanel />}
    </div>
  );
}
